import React from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { XCircle, X, AlertCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../i18n';

/**
 * Break-lease modal — different from buyout: the vehicle is RETURNED to the
 * lessor (removed from the fleet) and a 30% × remaining monthlies + 1 000 €
 * admin fee is charged.
 */
export const LeaseBreakModal = ({ vehicle, onClose }) => {
  const { t } = useTranslation();
  const computeLeaseBreak = useGameStore(s => s.computeLeaseBreak);
  const breakLease = useGameStore(s => s.breakLease);
  const treasury = useGameStore(s => s.treasury);

  const quote = computeLeaseBreak(vehicle.id);
  if (!quote) return null;
  const onMission = vehicle.status === 'active';
  const canAfford = treasury >= quote.total;
  const projectedTreasury = treasury - quote.total;

  const handleConfirm = () => {
    const result = breakLease(vehicle.id);
    if (result && result.ok !== false) onClose();
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="lease-break-overlay">
      <div className="bg-ink-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200" data-testid="lease-break-modal">
        <div className="bg-danger/90 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <XCircle className="w-5 h-5" />
            <h3 className="font-bold text-base">{t('break_lease_title')}</h3>
          </div>
          <button aria-label="Fermer" onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-break-lease">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-200">{vehicle.brand} {vehicle.model}</p>
            <p className="text-[11px] text-slate-500">Tier {vehicle.tier} · {quote.monthsRemaining} mois restants</p>
          </div>

          <p className="text-[12px] text-slate-400 leading-snug">{t('break_lease_intro')}</p>

          {onMission && (
            <div className="rounded-md border border-warn/40 bg-warn/5 px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-warn flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-warn">Ce véhicule est actuellement en mission. Résiliez d'abord le contrat.</p>
            </div>
          )}

          <div className="rounded-md border border-ink-600 bg-ink-700/30 divide-y divide-ink-600 text-xs">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('break_lease_penalty_label')}</span>
              <span className="font-bold text-danger" data-testid="break-lease-penalty">
                {formatMoney(quote.remainingMonthlies)} × 30 % = {formatMoney(quote.penalty)}
              </span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('break_lease_admin_fee')}</span>
              <span className="font-bold text-danger" data-testid="break-lease-admin-fee">+ {formatMoney(quote.adminFee)}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-danger/10">
              <span className="text-sm font-semibold text-danger">{t('break_lease_total')}</span>
              <span className="text-base font-black text-danger" data-testid="break-lease-total">{formatMoney(quote.total)}</span>
            </div>
          </div>

          <div className="rounded-md border border-ink-600 bg-ink-700/30 p-2.5 text-xs">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('treasury')} (post-rupture)</p>
            <p className={`text-base font-bold mt-0.5 ${projectedTreasury < 0 ? 'text-danger' : 'text-money'}`} data-testid="break-lease-projected-treasury">
              {formatMoney(projectedTreasury)}
            </p>
          </div>

          {!canAfford && (
            <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
              <p className="text-[12px] text-danger">{t('break_lease_insufficient')}</p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-between">
          <button onClick={onClose} className="text-xs text-slate-400 hover:text-slate-200 px-3 py-2 transition-colors" data-testid="cancel-break-lease">
            {t('cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canAfford || onMission}
            className="bg-danger text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="confirm-break-lease-btn"
          >
            <XCircle className="w-4 h-4" />
            {t('break_lease_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaseBreakModal;
